import { AppBar, Box, IconButton, Snackbar, Toolbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { AGENCY_ROLE, EMPLOYEE_ROLES, RESOURCE_SECTION } from "constants/constants";
import useUser from "hooks/userHooks";
import RECRUIT_ROUTES from "modules/Recruit/constants/routes";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { logoutEmployee } from "services/auth";
import {
  mixpanelInitialize,
  mixpanelLogoutEvent,
  mixpanelRegister,
} from "services/mixpanel";
import { Avatar, DropdownMenu, Loader } from "subframe";
import { SidebarWithCollapsibleSections } from "subframe/components/SidebarWithCollapsibleSections";
import { get, remove } from "utils/localStorage";
import { hasRecruitAccess, hasRetainAccess } from "utils/redirectUtils";
import {
  getAcronym,
  getFullName
} from "utils/string";
import Logo from "../../../assets/images/logoV2.svg";
import { AGENCIES_WITH_EXIT_SURVEY, GRAHAM_HEALTHCARE_GROUP_ID } from "../constants/agency";
import RETENTION_ROUTES, { getHomeRoute } from "../constants/routes";
import { useQueryPrefetchEscalations } from "../hooks/useQueryPrefetchEscalations";
import SearchEmployeesDialog from "./SearchEmployeesDialog";
import SidebarSkeleton from "./SidebarSkeleton";

export default function RetentionSideBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const searchEmployeesDialogRef = useRef(null);
  const { user, isLoading: isLoadingUser } = useUser();
  const theme = useTheme();

  const userForPermissions = {
    role: get('userRole'),
    configuration: user?.configuration || {},
  };

  useQueryPrefetchEscalations({ enabled: hasRetainAccess(userForPermissions) });

  // Check if current user is Graham Healthcare Group
  const isGrahamHealthcareGroup = user?.agency_id === GRAHAM_HEALTHCARE_GROUP_ID;

  const SIDEBAR_ROUTES = [
    {
      path: RECRUIT_ROUTES.CANDIDATES,
      label: 'Candidates',
      icon: 'FeatherUsers',
      section: RESOURCE_SECTION.RECRUIT,
    },
    {
      path: getHomeRoute(user?.agency_id),
      label: 'Home',
      icon: 'FeatherHome',
      section: RESOURCE_SECTION.RETAIN,
    },
    {
      path: RETENTION_ROUTES.SEARCH,
      label: 'Search',
      icon: 'FeatherSearch',
      section: RESOURCE_SECTION.RETAIN,
      showCondition: user?.configuration?.enable_employee_search,
      onClick: () => searchEmployeesDialogRef.current?.setOpen(true),
    },
    {
      path: RETENTION_ROUTES.TASK_CENTER,
      label: 'Task Center',
      icon: 'FeatherListChecks',
      section: RESOURCE_SECTION.RETAIN,
      showCondition: !isGrahamHealthcareGroup, // Hide task center for Graham Healthcare Group
    },
    {
      path: RETENTION_ROUTES.EXIT_SURVEY,
      label: 'Exit Survey',
      icon: 'FeatherFileBarChart',
      section: RESOURCE_SECTION.RETAIN,
      showCondition: AGENCIES_WITH_EXIT_SURVEY.includes(user?.agency_id),
    },
    {
      path: RETENTION_ROUTES.REPORTS,
      label: 'Reports',
      icon: 'FeatherAlignHBottom',
      section: RESOURCE_SECTION.RETAIN,
      showCondition: () => {
        const userRole = get('userRole');
        const isAdmin = userRole === EMPLOYEE_ROLES.admin.value;
        const isAgency = userRole === AGENCY_ROLE.agency.value;
        return !AGENCIES_WITH_EXIT_SURVEY.includes(user?.agency_id) && (isAdmin || isAgency);
      },
    },
    {
      path: RETENTION_ROUTES.EMPLOYEES,
      label: 'Employees',
      icon: 'FeatherUserCircle',
      section: RESOURCE_SECTION.RETAIN,
    },
    {
      path: RETENTION_ROUTES.SETTINGS,
      label: 'Settings',
      icon: 'FeatherSettings',
      section: RESOURCE_SECTION.RETAIN,
    },
    // Example for nested routes
    // {
    //   path: "/settings",
    //   label: "Settings",
    //   icon: "FeatherSettings",
    //   children: [
    //     {
    //       path: "/settings/admin-permissions",
    //       label: "Admin & Permissions",
    //       icon: "FeatherKeySquare",
    //     },
    //     {
    //       path: "/settings/system-settings",
    //       label: "System settings",
    //       icon: "FeatherWrench",
    //     },
    //   ],
    // },
  ];

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isErrorMessageShown, setIsErrorMessageShown] = useState(false);

  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      mixpanelInitialize();
      mixpanelRegister(user);
    }
  }, [user]);

  const handleLogout = async () => {
    const userRole = get("userRole");
    const email = get("email");

    if (Object.keys(EMPLOYEE_ROLES).includes(userRole)) {
      setIsLoggingOut(true);

      try {
        await logoutEmployee(email);
      } catch (error) {
        setErrorMessage(`Error while logging out: ${error?.message}`);
        setIsErrorMessageShown(true);

        return;
      } finally {
        setIsLoggingOut(false);
      }
    }

    queryClient.clear();
    
    remove("token");
    remove("refreshToken");
    remove("userRole");
    remove("email");
    remove("agency_name");
    remove("full_name");
    
    navigate("/");
    
    mixpanelLogoutEvent();
  };

  const getLoggedInUserInfo = () => {
    const userRole = get("userRole");

    // if (Object.keys(EMPLOYEE_ROLES).includes(userRole)) {
    //   return (
    //     <div className="flex flex-col items-start gap-1">
    //       <span className="text-body-bold font-body-bold text-default-font">
    //         {getFullName(user?.first_name, user?.last_name)}
    //       </span>
    //       <div className="flex flex-col items-start gap-1">
    //         <div className="flex items-center gap-2">
    //           <SubframeCore.Icon
    //             className="text-label font-label text-subtext-color"
    //             name="FeatherUserCog"
    //           />
    //           <span className="text-label font-label text-subtext-color">
    //             {isStringEmpty(EMPLOYEE_ROLES[user?.["role-type"]]?.label)
    //               ? "-"
    //               : EMPLOYEE_ROLES[user?.["role-type"]]?.label}
    //           </span>
    //         </div>
    //         <div className="flex items-center gap-2">
    //           <SubframeCore.Icon
    //             className="text-label font-label text-subtext-color"
    //             name="FeatherMail"
    //           />
    //           <span className="text-label font-label text-subtext-color">
    //             {isStringEmpty(user?.email) ? "-" : user.email}
    //           </span>
    //         </div>
    //         <div className="flex items-center gap-2">
    //           <SubframeCore.Icon
    //             className="text-label font-label text-subtext-color"
    //             name="FeatherMapPin"
    //           />
    //           <span className="text-label font-label text-subtext-color">
    //             {isStringEmpty(user?.region) ? "-" : user.region}
    //           </span>
    //         </div>
    //       </div>
    //     </div>
    //   );
    // }

    return (
      <div className="flex flex-col min-w-0 w-full">
      <span className="text-body-bold font-body-bold text-default-font truncate">
        {user?.name ? user.name : getFullName(user?.first_name, user?.last_name)}
      </span>
      <span className="text-label font-label text-subtext-color truncate">
        {user?.email}
      </span>
    </div>
    );
  };

  const getLoggedInUserAvatar = () => {
    const userRole = get("userRole");

    if (Object.keys(EMPLOYEE_ROLES).includes(userRole)) {
      return (
        <Avatar className="cursor-pointer">
          {getAcronym(getFullName(user?.first_name, user?.last_name))}
        </Avatar>
      );
    }

    return <Avatar className="cursor-pointer">{getAcronym(user?.name)}</Avatar>;
  };

  /**
   * Renders the error snackbar.
   *
   * @returns {Component}
   */
  const renderErrorSnackbar = () => (
    <Snackbar
      open={isErrorMessageShown}
      autoHideDuration={6000}
      onClose={() => setIsErrorMessageShown(false)}
    >
      <MuiAlert
        onClose={() => setIsErrorMessageShown(false)}
        severity="error"
        sx={{ width: "100%" }}
      >
        {errorMessage}
      </MuiAlert>
    </Snackbar>
  );

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const renderSidebarItems = (routes) => {
    if (isLoadingUser) {
      return <SidebarSkeleton />;
    }

    // Filter out routes that shouldn't be shown before mapping
    const visibleRoutes = routes.filter(route => {
      if (route.hasOwnProperty('showCondition')) {
        if (typeof route.showCondition === 'function') {
          return route.showCondition();
        }
        return route.showCondition;
      }
      return true;
    });

    const recruitRoutes = visibleRoutes.filter(route => route.section === RESOURCE_SECTION.RECRUIT);
    const retainRoutes = visibleRoutes.filter(route => route.section === RESOURCE_SECTION.RETAIN);

    return (
      <div className="flex w-full flex-col items-start gap-4 pt-4">
        {/* RECRUIT Section */}
        {hasRecruitAccess(userForPermissions) && recruitRoutes.length > 0 && (
          <>
            <div className="flex w-full flex-col items-start gap-1">
              <span className="font-['Nunito'] text-[12px] font-[400] leading-[16px] text-subtext-color">
                {RESOURCE_SECTION.RECRUIT}
              </span>
              {recruitRoutes.map(route => {
                if (route.children) {
                  return (
                    <SidebarWithCollapsibleSections.NavSection
                      open={false}
                      key={route.path}
                      label={route.label}
                      selected={location.pathname.includes(route.path)}
                      icon={route.icon}
                    >
                      {route.children.map(child => (
                        <SidebarWithCollapsibleSections.NavItem
                          key={child.path}
                          icon={child.icon}
                          selected={location.pathname.includes(child.path)}
                          onClick={() => {
                            if (child.onClick) {
                              child.onClick();
                            } else {
                              navigate(child.path);
                            }
                          }}
                        >
                          {child.label}
                        </SidebarWithCollapsibleSections.NavItem>
                      ))}
                    </SidebarWithCollapsibleSections.NavSection>
                  );
                }

                const selected =
                  route.label === 'Home'
                    ? location.pathname === route.path
                    : location.pathname.includes(route.path);
                return (
                  <SidebarWithCollapsibleSections.NavItem
                    key={route.path}
                    open={false}
                    icon={route.icon}
                    selected={selected}
                    onClick={() => {
                      if (route.onClick) {
                        route.onClick();
                      } else {
                        navigate(route.path);
                      }
                    }}
                  >
                    {route.label}
                  </SidebarWithCollapsibleSections.NavItem>
                );
              })}
            </div>
            <div className="hidden h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
          </>
        )}

        {/* RETAIN Section */}
        {hasRetainAccess(userForPermissions) && retainRoutes.length > 0 && (
          <div className="flex w-full flex-col items-start gap-1">
            <span className="font-['Nunito'] text-[12px] font-[400] leading-[16px] text-subtext-color">
              {RESOURCE_SECTION.RETAIN}
            </span>

            {retainRoutes.map(route => {
              if (route.children) {
                return (
                  <SidebarWithCollapsibleSections.NavSection
                    open={false}
                    key={route.path}
                    label={route.label}
                    selected={location.pathname.includes(route.path)}
                    icon={route.icon}
                  >
                    {route.children.map(child => (
                      <SidebarWithCollapsibleSections.NavItem
                        key={child.path}
                        icon={child.icon}
                        selected={location.pathname.includes(child.path)}
                        onClick={() => {
                          if (child.onClick) {
                            child.onClick();
                          } else {
                            navigate(child.path);
                          }
                        }}
                      >
                        {child.label}
                      </SidebarWithCollapsibleSections.NavItem>
                    ))}
                  </SidebarWithCollapsibleSections.NavSection>
                );
              }

              const selected =
                route.label === 'Home'
                  ? location.pathname === route.path
                  : location.pathname.includes(route.path);
              return (
                <SidebarWithCollapsibleSections.NavItem
                  key={route.path}
                  open={false}
                  icon={route.icon}
                  selected={selected}
                  onClick={() => {
                    if (route.onClick) {
                      route.onClick();
                    } else {
                      navigate(route.path);
                    }
                  }}
                >
                  {route.label}
                </SidebarWithCollapsibleSections.NavItem>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1199,
            }}
            onClick={toggleSidebar}
          />
        )}

        {/* Sidebar */}
        <Box
          sx={{
            display: { xs: isSidebarOpen ? "block" : "none", md: "block" },
            width: { xs: "280px", md: "auto" },
            position: { xs: "fixed", md: "relative" },
            zIndex: { xs: 1200, md: "auto" },
            height: "100%",
            left: { xs: 0, md: "auto" },
            top: { xs: 0, md: "auto" },
          }}
          className="h-screen"
        >
          <SidebarWithCollapsibleSections
            className="h-screen w-64 flex-none self-stretch overflow-y-auto bg-[#f5f9ffff]"
            header={
              <div className="flex w-full flex-col items-start justify-center gap-2 px-4 py-4">
                <img
                  className="h-7 flex-none object-contain"
                  src={Logo}
                  alt="levo-logo"
                />
              </div>
            }
            footer={
              <>
                {!isLoadingUser && (
                  <SubframeCore.DropdownMenu.Root>
                    <SubframeCore.DropdownMenu.Trigger asChild={true}>
                      <div className="flex w-full items-center gap-4 hidden md:flex">
                        {getLoggedInUserAvatar()}
                        <div className="flex flex-col min-w-0 flex-1">
                          {getLoggedInUserInfo()}
                        </div>
                        <SubframeCore.Icon
                          className="text-label font-label text-default-font flex-shrink-0"
                          name="FeatherChevronsUpDown"
                        />
                      </div>
                    </SubframeCore.DropdownMenu.Trigger>
                    <SubframeCore.DropdownMenu.Portal>
                      <SubframeCore.DropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        asChild={true}
                      >
                        <DropdownMenu>
                          <div className="grow shrink-0 basis-0 h-full w-full flex-col items-start flex">
                            <div className="grow shrink-0 basis-0 h-full w-full flex-col pt-2 pr-3 pb-2 pl-3 items-start flex">
                              {getLoggedInUserInfo()}
                            </div>
                            <div className="w-full flex-col pt-0.5 pb-0.5 items-start flex">
                              <div className="flex-none h-px w-full flex-col gap-2 items-center flex bg-neutral-300" />
                            </div>
                            <div
                              className="w-full h-8 gap-1.5 pr-3 pl-3 justify-between items-center flex cursor-pointer hover:bg-neutral-100 data-[highlighted]:bg-neutral-100"
                              onClick={handleLogout}
                            >
                              <div className="flex items-center gap-1.5">
                                <SubframeCore.Icon
                                  className="text-neutral-700 text-body font-body"
                                  name="FeatherLogOut"
                                />
                                <span className="text-neutral-700 text-body font-body">
                                  Log Out
                                </span>
                              </div>
                              {isLoggingOut && <Loader />}
                            </div>
                          </div>
                        </DropdownMenu>
                      </SubframeCore.DropdownMenu.Content>
                    </SubframeCore.DropdownMenu.Portal>
                  </SubframeCore.DropdownMenu.Root>
                )}
              </>
            }
          >
            <div className="flex w-full flex-col items-start gap-1 border-b border-neutral-border pb-6">
              {renderSidebarItems(SIDEBAR_ROUTES)}
            </div>
          </SidebarWithCollapsibleSections>
        </Box>

        {/* Main Content */}
        <div className="flex-1 h-screen flex flex-col overflow-scroll">
          <AppBar
            position="sticky"
            sx={{
              top: 0,
              zIndex: 99,
              height: "75px",
              backgroundColor: "#eff6ff",
              color: "#2563eb",
              boxShadow: "none",
              fontFamily: "Nunito",
            }}
            className="block md:!hidden"
          >
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Box display="flex" alignItems="center" gap={2}>
                <IconButton
                  onClick={toggleSidebar}
                  sx={{ color: "#2563eb" }}
                >
                  <SubframeCore.Icon name="FeatherMenu" />
                </IconButton>
                <img src={Logo} className="h-6 flex-none" alt="levo-logo" />
              </Box>

              {!isLoadingUser && (
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true} className="sm:hidden">
                    {getLoggedInUserAvatar()}
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="end"
                      sideOffset={8}
                      asChild={true}
                      style={{ 
                        zIndex: 9999,
                        position: 'fixed',
                        right: '16px',
                        top: '-10px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "200px",
                          padding: theme.spacing(1),
                        }}
                      >
                        <Box
                          sx={{
                            padding: theme.spacing(2),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          {getLoggedInUserInfo()}
                        </Box>
                        <Box
                          sx={{
                            padding: theme.spacing(1, 3),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "#f0f0f0",
                            },
                          }}
                          onClick={handleLogout}
                        >
                          <SubframeCore.Icon
                            name="FeatherLogOut"
                            style={{ color: "#555" }}
                          />
                          <span className="text-neutral-700 text-body font-body">
                            Log Out
                          </span>
                          {isLoggingOut && <Loader />}
                        </Box>
                      </Box>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              )}
            </Toolbar>
          </AppBar>
          <div className="flex-1 overflow-y-auto">
            <Outlet />
          </div>
        </div>
      </Box>
      <SearchEmployeesDialog ref={searchEmployeesDialogRef} />
      {renderErrorSnackbar()}
    </>
  );
}
